// admin/DebugLogsPanel.tsx
import { API_URL } from 'helpers/api_helper';
import React, { useEffect, useState, useCallback } from 'react';

interface LogEntry {
  timestamp: string;
  level: string;
  category?: string;
  message: string;
  employeeId?: string;
  sessionId?: string;
  reason?: string;
  data?: any;
  deviceInfo?: any;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const DebugLogsPanel: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<LogEntry | null>(null);

  // Filters
  const [employeeId, setEmployeeId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [level, setLevel] = useState('');
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');

  // Employee list for dropdown
  const [employees, setEmployees] = useState<any[]>([]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/mobile/debug/employees`);
      const json = await res.json();
      console.log('employee',json)
      if (json.success) setEmployees(json.employees || []);
    } catch {}
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (employeeId) params.set('employeeId', employeeId);
      if (sessionId) params.set('sessionId', sessionId);
      if (level) params.set('level', level);
      if (reason) params.set('reason', reason);
      if (search) params.set('search', search);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      params.set('sort', sort);

      const res = await fetch(`${API_URL}/mobile/debug/recent?${params.toString()}`);
      const json = await res.json();
      if (json.success) {
        setLogs(json.logs || []);
        setPagination(json.pagination || null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [employeeId, sessionId, level, reason, search, from, to, page, pageSize, sort]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const resetFilters = () => {
    setEmployeeId(''); setSessionId(''); setLevel(''); setReason('');
    setSearch(''); setFrom(''); setTo(''); setPage(1);
  };

  const levelBadge = (lvl: string) => {
    const map: any = {
      ERROR: 'bg-danger',
      FATAL: 'bg-danger',
      WARN: 'bg-warning text-dark',
      INFO: 'bg-info text-dark',
      DEBUG: 'bg-secondary',
    };
    return <span className={`badge ${map[lvl] || 'bg-secondary'}`}>{lvl}</span>;
  };

  return (
    <div className="p-3">
      <h4>🐞 Mobile Debug Logs</h4>

      {/* -------- Filters -------- */}
      <div className="row g-2 mb-3">
        <div className="col-md-2">
          <label className="form-label small">Employee</label>
          <select className="form-select form-select-sm" value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setPage(1); }}>
            <option value="">All</option>
            {employees.map((emp) => (
              <option key={emp.employeeId} value={emp.employeeId}>
                {emp.employeeId} ({emp.total} / {emp.errors} err)
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label small">Session ID</label>
          <input className="form-control form-control-sm" value={sessionId} onChange={(e) => { setSessionId(e.target.value); setPage(1); }} placeholder="face_..." />
        </div>
        <div className="col-md-1">
          <label className="form-label small">Level</label>
          <select className="form-select form-select-sm" value={level} onChange={(e) => { setLevel(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="ERROR">ERROR</option>
            <option value="FATAL">FATAL</option>
            <option value="WARN">WARN</option>
            <option value="INFO">INFO</option>
            <option value="DEBUG">DEBUG</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label small">Reason</label>
          <input className="form-control form-control-sm" value={reason} onChange={(e) => { setReason(e.target.value); setPage(1); }} placeholder="periodic-5s, capture-success..." />
        </div>
        <div className="col-md-2">
          <label className="form-label small">Search message</label>
          <input className="form-control form-control-sm" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="no face, camera..." />
        </div>
        <div className="col-md-1">
          <label className="form-label small">From</label>
          <input type="date" className="form-control form-control-sm" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        </div>
        <div className="col-md-1">
          <label className="form-label small">To</label>
          <input type="date" className="form-control form-control-sm" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        </div>
        <div className="col-md-1 d-flex align-items-end gap-1">
          <button className="btn btn-sm btn-outline-secondary w-100" onClick={resetFilters}>Reset</button>
        </div>
      </div>

      {/* -------- Meta -------- */}
      {pagination && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="text-muted">
            Total {pagination.total} logs · page {pagination.page}/{pagination.totalPages || 1}
          </small>
          <div className="d-flex gap-2 align-items-center">
            <select className="form-select form-select-sm" style={{ width: 90 }} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
            <select className="form-select form-select-sm" style={{ width: 110 }} value={sort} onChange={(e) => setSort(e.target.value as any)}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
        </div>
      )}

      {/* -------- Table -------- */}
      <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <table className="table table-sm table-hover align-middle">
          <thead className="table-light sticky-top">
            <tr>
              <th style={{ width: 160 }}>Timestamp</th>
              <th style={{ width: 90 }}>Level</th>
              <th style={{ width: 140 }}>Employee</th>
              <th style={{ width: 140 }}>Reason</th>
              <th>Message</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">No logs match filters</td></tr>
            ) : (
              logs.map((log, i) => (
                <tr key={i} style={{ cursor: 'pointer' }} onClick={() => setSelected(log)}>
                  <td><small>{new Date(log.timestamp).toLocaleString()}</small></td>
                  <td>{levelBadge(log.level)}</td>
                  <td><small>{log.employeeId || '-'}</small></td>
                  <td><small>{log.reason || '-'}</small></td>
                  <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <small>{log.message?.slice(0, 300)}{(log.message?.length || 0) > 300 ? '…' : ''}</small>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-link p-0">View</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* -------- Pagination -------- */}
      {pagination && pagination.totalPages > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-2">
          <button className="btn btn-sm btn-outline-secondary" disabled={!pagination.hasPrev} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span className="align-self-center small">Page {pagination.page} of {pagination.totalPages}</span>
          <button className="btn btn-sm btn-outline-secondary" disabled={!pagination.hasNext} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}

      {/* -------- Detail modal -------- */}
      {selected && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setSelected(null)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Log Detail</h6>
                <button className="btn-close" onClick={() => setSelected(null)} />
              </div>
              <div className="modal-body">
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, maxHeight: '60vh', overflow: 'auto' }}>
                  {JSON.stringify(selected, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugLogsPanel;